import {
  Box,
  List,
  ListItem,
  ListItemButton,
  Typography,
  type SxProps,
} from "@mui/material";
import type { ReactNode } from "react";
import { Link } from "wouter";

const idSx = { fontFamily: "monospace", fontWeight: 600 } as const;

export const DenseList = ({ children }: { children: ReactNode }) => (
  <List disablePadding sx={{ bgcolor: "background.paper" }}>
    {children}
  </List>
);

interface RowProps {
  // Monospace identifier at the start of line 1 (account, zone number, ...).
  id?: ReactNode;
  primary: ReactNode;
  secondary?: ReactNode;
  end?: ReactNode;
  href?: string;
  accent?: string | null;
  muted?: boolean;
  // Lines line 1 may wrap to before it is cut; 1 keeps it on one line.
  primaryLines?: number;
}

// Two lines at most; the whole row is the tap target. Density comes from
// typography, not from small hit areas.
export function Row({ id, primary, secondary, end, href, accent, muted, primaryLines = 1 }: RowProps) {
  const hasId = id !== undefined && id !== null && id !== "";
  const content = (
    <Box sx={{ display: "flex", alignItems: "center", width: "100%", minWidth: 0, gap: 1 }}>
      <Box sx={{ flex: 1, minWidth: 0, opacity: muted ? 0.6 : 1 }}>
        {primaryLines > 1 ? (
          // One inline flow, so wrapped lines use the full width under the id.
          <Typography
            variant="body2"
            component="div"
            sx={{
              display: "-webkit-box",
              WebkitLineClamp: primaryLines,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
              wordBreak: "break-word",
            }}
          >
            {hasId && (
              <Box component="span" sx={idSx}>
                {id}{" "}
              </Box>
            )}
            {primary}
          </Typography>
        ) : (
          <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.75, minWidth: 0 }}>
            {hasId && (
              <Typography variant="body2" component="span" sx={{ ...idSx, flexShrink: 0 }}>
                {id}
              </Typography>
            )}
            <Typography variant="body2" component="span" noWrap sx={{ minWidth: 0 }}>
              {primary}
            </Typography>
          </Box>
        )}
        {secondary && (
          <Typography variant="caption" component="div" color="text.secondary" noWrap>
            {secondary}
          </Typography>
        )}
      </Box>
      {end && <Box sx={{ flexShrink: 0 }}>{end}</Box>}
    </Box>
  );

  const sx: SxProps = {
    minHeight: 44,
    py: 0.5,
    px: 1.5,
    borderBottom: 1,
    borderColor: "divider",
    borderLeft: accent ? `4px solid ${accent}` : undefined,
  };

  return href ? (
    <ListItem disablePadding>
      <ListItemButton component={Link} href={href} sx={sx}>
        {content}
      </ListItemButton>
    </ListItem>
  ) : (
    <ListItem sx={sx}>{content}</ListItem>
  );
}

export const PhoneLink = ({ number }: { number: string | null | undefined }) =>
  number ? (
    <a href={`tel:${number.replace(/[^\d+]/g, "")}`} onClick={(e) => e.stopPropagation()}>
      {number}
    </a>
  ) : null;
